import React, { Suspense, lazy, useEffect } from 'react';

import { ErrorBoundary } from './components/Error';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import VoucherProvider from './store/voucherProvider';
import InvoiceProvider from './store/invoiceProvider';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import OfframpPaymentProvider from './store/offrampPaymentProvider';
import { useUser } from './hooks/useUser';
import OnrampPaymentProvider from './store/onrampPaymentProvider';
import V4ContextProvider from './store/v4ContextProvider';

const Pay = lazy(() => import('./components/Pay'));
const Footer = lazy(() => import('./components/Footer'));
const Header = lazy(() => import('./components/Header'));
const Expired = lazy(() => import('./components/Expired'));
const Success = lazy(() => import('./components/Success'));
const Error = lazy(() => import('./components/Error/Error'));
const NotFound = lazy(() => import('./components/Error/404'));
const WrongAmount = lazy(() => import('./components/WrongAmount'));
const Payment = lazy(() => import('./components/Payment'));
const Spinner = lazy(() => import('./components/spinner'));

const Demo = lazy(() => import('./components/Demo'));

// V4 routes
const V4Header = lazy(() => import('./components/v4/components/Header'));
const V4Payment = lazy(() => import('./components/v4'));
const V4Authentication = lazy(() => import('./components/v4/authentication'));
const V4Offramp = lazy(() => import('./components/v4/Offramp'));
const V4OfframpOrderSummary = lazy(
  () => import('./components/v4/Offramp/ngn/components/OrderSummary')
);
const OfframpPaymentV4 = lazy(
  () => import('./components/v4/Offramp/components/Payment')
);
const V4Onramp = lazy(() => import('./components/v4/Onramp'));
const OnrampPaymentV4 = lazy(
  () => import('./components/v4/Onramp/component/Payment')
);
const NGNOnrampOrderSummary = lazy(
  () => import('./components/v4/Onramp/ngn/screens/OrderSummary')
);
const NGNOnrampPayment = lazy(
  () => import('./components/v4/Onramp/ngn/screens/Payment')
);
const NGNOnrampProcessing = lazy(
  () => import('./components/v4/Onramp/ngn/screens/Processing')
);
const V4OnrampPaymentSuccess = lazy(
  () => import('./components/v4/Onramp/component/Success')
);
const V4OnrampPaymentPending = lazy(
  () => import('./components/v4/Onramp/component/PendingConfirmation')
);
const V4OnrampPaymentFailed = lazy(
  () => import('./components/v4/Onramp/component/Failed')
);
const V4OfframpPaymentSuccess = lazy(
  () => import('./components/v4/Offramp/components/Success')
);
const V4OfframpPendingConfirmation = lazy(
  () => import('./components/v4/Offramp/components/PendingConfirmation')
);
const V4OfframpPaymentFailed = lazy(
  () => import('./components/v4/Offramp/components/Failed')
);
const V4OfframpProcessing = lazy(
  () => import('./components/v4/Offramp/components/Processing')
);

//invoice
const InvoiceHeader = lazy(() => import('./components/Invoice/Header'));
const SignIn = lazy(() => import('./components/Invoice/SignIn'));
const CreateInvoice = lazy(
  () => import('./components/Invoice/CreateInvoice/index')
);
const InvoiceSuccess = lazy(() => import('./components/Invoice/Success'));

// Offramp
const OfframpIndex = lazy(() => import('./components/Offramp'));
const MakePayment = lazy(
  () => import('./components/Offramp/Offramp/components/Payment')
);
const MakePaymentForPayType = lazy(
  () => import('./components/Offramp/PayWithPartna/components/Payment')
);
const OfframpHeader = lazy(
  () => import('./components/Offramp/commonFeatures/Header')
);
const PaymentSuccess = lazy(
  () => import('./components/Offramp/Offramp/components/Success')
);

const PendingConfirmation = lazy(
  () => import('./components/Offramp/Offramp/components/PendingConfirmation')
);

const PaymentFailed = lazy(
  () => import('./components/Offramp/Offramp/components/Failed')
);

const PaymentsV4 = () => {
  return (
    <OfframpPaymentProvider>
      <OnrampPaymentProvider>
        <VoucherProvider>
          <V4ContextProvider>
            <div className="mainApp animate__animated animate__fadeIn td-200">
              <V4Header />
              <Routes>
                <Route path="/pay" element={<V4Payment />} />
                <Route path="/login" element={<V4Authentication />} />
                <Route path="/pay/offramp" element={<V4Offramp />} />
                <Route
                  path="/pay/offramp/orderSummary"
                  element={<V4OfframpOrderSummary />}
                />
                <Route path="/pay/onramp" element={<V4Onramp />} />
                <Route
                  path="/pay/onramp/ngn/summary"
                  element={<NGNOnrampOrderSummary />}
                />
                <Route
                  path="/pay/onramp/ngn/payment"
                  element={<NGNOnrampPayment />}
                />
                <Route
                  path="/pay/onramp/processing"
                  element={<NGNOnrampProcessing />}
                />
                <Route
                  path="/pay/onramp/makePayment"
                  element={<OnrampPaymentV4 />}
                />
                <Route
                  path="/pay/offramp/makePayment"
                  element={<OfframpPaymentV4 />}
                />
                <Route
                  path="/pay/offramp/pendingconfirmation"
                  element={<V4OfframpPendingConfirmation />}
                />
                <Route
                  path="/pay/offramp/success"
                  element={<V4OfframpPaymentSuccess />}
                />
                <Route
                  path="/pay/offramp/failed"
                  element={<V4OfframpPaymentFailed />}
                />
                <Route
                  path="/pay/offramp/processing"
                  element={<V4OfframpProcessing />}
                />
                <Route
                  path="/pay/onramp/success"
                  element={<V4OnrampPaymentSuccess />}
                />
                <Route
                  path="/pay/onramp/pendingconfirmation"
                  element={<V4OnrampPaymentPending />}
                />
                <Route
                  path="/pay/onramp/failed"
                  element={<V4OnrampPaymentFailed />}
                />

                {/* <Route path="/pay/onramp/makePayment" element={<OnrampPayment />} /> */}
                {/* <Route path="/*" element={<NotFound />} /> */}
              </Routes>
              <Footer />
            </div>
          </V4ContextProvider>
        </VoucherProvider>
      </OnrampPaymentProvider>
    </OfframpPaymentProvider>
  );
};

const Payments = () => {
  return (
    <VoucherProvider>
      <div className="mainApp animate__animated animate__fadeIn td-200">
        <Header />
        <Routes>
          <Route path="/pay" element={<Pay />} />
          <Route path="/wrongamount" element={<WrongAmount />} />
          <Route path="/expired" element={<Expired />} />
          <Route path="/success" element={<Success />} />
        </Routes>
        <Footer />
      </div>
    </VoucherProvider>
  );
};

const Invoice = () => {
  return (
    <InvoiceProvider>
      <div className="mainApp animate__animated animate__fadeIn td-200">
        <InvoiceHeader />

        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/create" element={<CreateInvoice />} />
          <Route path="/success" element={<InvoiceSuccess />} />
        </Routes>
      </div>
    </InvoiceProvider>
  );
};

const Offramp = () => {
  const posthog = usePostHog();
  const { email, merchant } = useUser();

  useEffect(() => {
    if (email || merchant) {
      // Identify sends an event, so you may want to limit how often you call it
      posthog?.identify(email, {
        email,
        merchant,
      });
      posthog?.group('merchant', merchant);
    }
  }, [posthog, email, merchant]);

  return (
    <OfframpPaymentProvider>
      <div className="mainApp animate__animated animate__fadeIn td-200">
        <OfframpHeader />
        <Routes>
          <Route path="/pay" element={<OfframpIndex />} />
          <Route path="/makePayment" element={<MakePayment />} />
          <Route path="pay/makePayment" element={<MakePaymentForPayType />} />
          <Route path="/success" element={<PaymentSuccess />} />
          <Route
            path="/pendingconfirmation"
            element={<PendingConfirmation />}
          />
          <Route path="/failed" element={<PaymentFailed />} />
        </Routes>
        <Footer />
      </div>
    </OfframpPaymentProvider>
  );
};

// const LandingpageHeader = lazy(() => import('./components/LandingPage/Header'));
// const OnrampPage = lazy(() => import('./components/LandingPage/OnrampPage'));
// const OfframpPage = lazy(() => import('./components/LandingPage/OfframpPage'));
const Landingpage = lazy(() => import('./components/LandingPage/landingPage'));
const Ping = lazy(() => import('./components/Ping'));

// const LandingpageRoute = () => {
//   return (
//     <div className="mainApp animate__animated animate__fadeIn td-200">
//       <LandingpageHeader />
//       <Suspense
//         fallback={
//           <div
//             style={{
//               marginTop: 30,
//               width: 390,
//             }}
//           />
//         }
//       >
//         <Routes>
//           <Route path="/" element={<Landingpage />} />
//           <Route path="/buy-crypto" element={<OnrampPage />} />
//           <Route path="/sell-crypto" element={<OfframpPage />} />
//         </Routes>
//       </Suspense>
//       <Footer />
//     </div>
//   );
// };

const PartnerDemo = () => {
  return (
    <div className="mainApp animate__animated animate__fadeIn td-200">
      <Suspense
        fallback={
          <div
            style={{
              marginTop: 30,
              width: 390,
            }}
          />
        }
      >
        <Routes>
          <Route path="/" element={<Demo />} />
        </Routes>
      </Suspense>
      <Footer />
    </div>
  );
};

const options = {
  api_host: 'https://eu.posthog.com',
};

const App = () => {
  return (
    <>
      <PostHogProvider
        apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ''}
        options={options}
      >
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<Spinner />}>
              <Routes>
                {/* <Route path="/" element={<LandingpageRoute />} /> */}
                <Route path="/" element={<Landingpage />} />
                <Route path="/ping" element={<Ping />} />
                <Route path="/demo" element={<PartnerDemo />} />
                <Route path="/error" element={<Error />} />
                <Route path="/payment/:id" element={<Payment />} />
                <Route path="/voucher/*" element={<Payments />} />
                <Route path="/v4/*" element={<PaymentsV4 />} />
                <Route path="/demo/voucher/*" element={<Payments />} />
                <Route path="/invoice/*" element={<Invoice />} />
                <Route path="/:version/voucher/*" element={<Payments />} />
                <Route path="/offramp/*" element={<Offramp />} />
                {/* <Route path="/service/*" element={<LandingpageRoute />} /> */}
                <Route path="/demo/offramp/*" element={<Offramp />} />
                <Route path="/service/*" element={<Landingpage />} />
                <Route path="/*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </PostHogProvider>
    </>
  );
};

export default App;
